import { useEffect, useState } from 'react';
import { Badge } from '@vnetwork/ui/primitives';
import { rotationStateAt, recentPeriods } from '@/api/aes';
import { fmtCountdown, fmtDuration } from '../shared';
import styles from '../Drm.module.css';

/**
 * Live LIVE-stream rotation view. Rotation is a pure clock function of
 * period_secs (period = floor(unix/period_secs)), so there is NOTHING to poll —
 * a 1s client tick re-derives the current period, countdown and timeline.
 */
export function RotationLiveView({
  streamId,
  periodSecs,
  dvrSeconds,
}: {
  streamId: string;
  periodSecs: number;
  dvrSeconds: number;
}) {
  // 1s tick — drives countdown + period rollover with no network calls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!periodSecs || periodSecs <= 0) {
    return (
      <p className={styles.metaLine}>
        This stream has no rotation period configured (VOD or static key).
      </p>
    );
  }

  const st = rotationStateAt(periodSecs, now);
  const periods = recentPeriods(periodSecs, dvrSeconds, 12, now);
  const pct = Math.min(100, Math.max(0, Math.round(st.progress * 100)));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div className={styles.rotationGrid}>
        <div className={styles.rotationCard}>
          <span className={styles.cardLabel}>Current period</span>
          <span className={styles.periodValue}>#{st.currentPeriod}</span>
          <span className={styles.metaLine}>
            <code>floor(unix / {periodSecs})</code>
          </span>
        </div>
        <div className={styles.rotationCard}>
          <span className={styles.cardLabel}>Next rotation in</span>
          <span className={styles.countdownValue}>{fmtCountdown(st.secondsToNext)}</span>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <p className={styles.metaLine}>
        Period <code>{fmtDuration(periodSecs)}</code> · DVR window{' '}
        <code>{fmtDuration(dvrSeconds)}</code> · key ={' '}
        <code>HKDF(master, &quot;{streamId}&quot; ‖ period)</code>
      </p>

      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        <div className={styles.legend}>
          <span>
            <span className={styles.legendDot} style={{ background: 'var(--vn-primary-subtle)', border: '1px solid var(--vn-primary-border)' }} />
            live edge
          </span>
          <span>
            <span className={styles.legendDot} style={{ background: 'var(--vn-success-subtle)', border: '1px solid var(--vn-success-border)' }} />
            in DVR window (re-fetchable)
          </span>
          <span>
            <span className={styles.legendDot} style={{ background: 'var(--vn-bg-subtle)', border: '1px dashed var(--vn-border-default)' }} />
            aged out
          </span>
        </div>
        <div className={styles.timeline}>
          {periods.map((p) => {
            const cls = p.isCurrent
              ? `${styles.tick} ${styles.tickCurrent}`
              : p.inDvrWindow
                ? `${styles.tick} ${styles.tickInDvr}`
                : `${styles.tick} ${styles.tickAged}`;
            const title = `period #${p.period} · ${new Date(p.startsAt * 1000).toLocaleTimeString()} → ${new Date(p.endsAt * 1000).toLocaleTimeString()}`;
            return (
              <span key={p.period} className={cls} title={title}>
                #{p.period}
              </span>
            );
          })}
        </div>
        <Badge tone="neutral" appearance="subtle" size="sm">
          {periods.filter((p) => p.inDvrWindow).length} period(s) reproducible for DVR rewind
        </Badge>
      </div>
    </div>
  );
}
